import * as THREE from 'three';
import { FullscreenPass } from '../gpu';

// Procedural PBR textures, baked once on the GPU at start-up. Each texture
// set is one height/colour function evaluated three times: albedo (sRGB, alpha
// = paint-wear mask), tangent-space normal (from the height field) and ORM
// (occlusion, roughness, metalness). Occlusion and roughness vary slowly, so
// the ORM map is baked at half resolution.

export interface TextureSet {
  map: THREE.Texture;
  normalMap: THREE.Texture;
  orm: THREE.Texture;
  /** Metres covered by one repeat (u, v). */
  size: THREE.Vector2;
}

const NOISE = /* glsl */ `
float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
vec2 hash22(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.xx + p3.yz) * p3.zy);
}
// Tileable value noise with period 'per' (in lattice units).
float vnoise(vec2 p, vec2 per) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash12(mod(i, per));
  float b = hash12(mod(i + vec2(1.0, 0.0), per));
  float c = hash12(mod(i + vec2(0.0, 1.0), per));
  float d = hash12(mod(i + vec2(1.0, 1.0), per));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}
float fbm(vec2 p, vec2 per, int oct) {
  float s = 0.0, a = 0.5, n = 0.0;
  for (int i = 0; i < 8; i++) {
    if (i >= oct) break;
    s += vnoise(p, per) * a;
    n += a;
    a *= 0.5;
    p *= 2.0;
    per *= 2.0;
  }
  return s / n;
}
float worleyT(vec2 p, vec2 per) {
  vec2 i = floor(p), f = fract(p);
  float md = 8.0;
  for (int y = -1; y <= 1; y++) for (int x = -1; x <= 1; x++) {
    vec2 o = vec2(x, y);
    vec2 r = o + hash22(mod(i + o, per)) - f;
    md = min(md, dot(r, r));
  }
  return sqrt(md);
}
// Fine pores and fibres of wood, stretched along the grain (x).
float pores(vec2 m, vec2 T) {
  return smoothstep(0.62, 0.8, vnoise(vec2(m.x * 9.0, m.y * 260.0), T * vec2(9.0, 260.0)));
}
// A knot at c (metres) of radius r: returns the dark heart (x) and how much
// the grain bends round it (y).
vec2 knot(vec2 m, vec2 c, float r) {
  vec2 d = (m - c) / vec2(r * 2.6, r);
  float k = length(d);
  return vec2(1.0 - smoothstep(0.35, 1.0, k), exp(-k * k * 0.7));
}
`;

/**
 * Frame for a texture function. The body must define:
 *   void surface(vec2 m, out vec3 albedo, out float height, out vec3 orm, out float wear)
 * where m is the position in metres within the tile.
 */
function bakeShader(body: string): string {
  return /* glsl */ `
uniform vec2 uSize;
uniform vec2 uRes;
uniform int uOut;
in vec2 vUv;
${NOISE}
${body}
void main() {
  vec2 m = vUv * uSize;
  vec3 albedo; float h; vec3 orm; float wear;
  surface(m, albedo, h, orm, wear);
  if (uOut == 0) { gl_FragColor = vec4(albedo, wear); return; }
  if (uOut == 2) { gl_FragColor = vec4(orm, 1.0); return; }
  vec2 e = uSize / uRes;
  vec3 a2; vec3 o2; float w2;
  float hx0, hx1, hy0, hy1;
  surface(m - vec2(e.x, 0.0), a2, hx0, o2, w2);
  surface(m + vec2(e.x, 0.0), a2, hx1, o2, w2);
  surface(m - vec2(0.0, e.y), a2, hy0, o2, w2);
  surface(m + vec2(0.0, e.y), a2, hy1, o2, w2);
  // Height is in metres; convert to a tangent-space normal.
  vec3 n = normalize(vec3(-(hx1 - hx0) / (2.0 * e.x), -(hy1 - hy0) / (2.0 * e.y), 1.0));
  gl_FragColor = vec4(n * 0.5 + 0.5, 1.0);
}
`;
}

function bake(renderer: THREE.WebGLRenderer, body: string, w: number, h: number, sizeM: [number, number]): TextureSet {
  const pass = new FullscreenPass(bakeShader(body), {
    uSize: { value: new THREE.Vector2(sizeM[0], sizeM[1]) },
    uRes: { value: new THREE.Vector2(w, h) },
    uOut: { value: 0 },
  });
  const make = (srgb: boolean, scale: number) =>
    new THREE.WebGLRenderTarget(Math.max(64, Math.round(w * scale)), Math.max(64, Math.round(h * scale)), {
      type: THREE.UnsignedByteType,
      colorSpace: srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace,
      wrapS: THREE.RepeatWrapping,
      wrapT: THREE.RepeatWrapping,
      minFilter: THREE.LinearMipmapLinearFilter,
      magFilter: THREE.LinearFilter,
      generateMipmaps: true,
      anisotropy: renderer.capabilities.getMaxAnisotropy(),
      depthBuffer: false,
    });
  const out = [make(true, 1), make(false, 1), make(false, 0.5)];
  for (let i = 0; i < 3; i++) {
    pass.uniforms.uOut!.value = i;
    pass.render(renderer, out[i]!);
  }
  pass.dispose();
  renderer.setRenderTarget(null);
  return { map: out[0]!.texture, normalMap: out[1]!.texture, orm: out[2]!.texture, size: new THREE.Vector2(sizeM[0], sizeM[1]) };
}

// ---------------------------------------------------------------- surfaces

/**
 * Carvel hull planking: oak strakes of varied length and tone with knots,
 * open pores and ring figure; seams payed with oakum and pitch; treenails and
 * iron bolts at the butts weeping rust; salt bloom, grime streaking down from
 * every seam; and paint that wears through at edges, bolts and scuffs.
 */
const HULL = /* glsl */ `
void surface(vec2 m, out vec3 albedo, out float height, out vec3 orm, out float wear) {
  vec2 T = uSize;
  float pw = 0.25;
  float row = floor(m.y / pw);
  float fy = fract(m.y / pw);
  float rh = hash12(vec2(row, 3.0));
  float len = T.x / (1.0 + floor(rh * 2.0));
  float xo = m.x + rh * T.x;
  float pi = floor(xo / len);
  float fx = fract(xo / len);
  float ph = hash12(vec2(row, pi + 17.0));
  float ph2 = hash12(vec2(pi, row + 31.0));
  // Seams between strakes and at butt joints: pitch with oakum showing at the lip.
  float seamY = min(fy, 1.0 - fy) * pw;
  float seamX = min(fx, 1.0 - fx) * len;
  float sd = min(seamY, seamX);
  float seam = 1.0 - smoothstep(0.002, 0.0065, sd);
  float oakum = (1.0 - smoothstep(0.0055, 0.0085, sd)) * (1.0 - seam) * (0.5 + 0.5 * vnoise(m * vec2(60.0, 60.0), T * 60.0));
  // A knot or two in some planks, bending the grain round them.
  vec2 kc = vec2((floor(xo / len) + 0.2 + 0.6 * ph2) * len - rh * T.x, (row + 0.35 + 0.3 * ph) * pw);
  kc.x = mod(kc.x, T.x);
  vec2 dk = m - kc;
  dk.x -= T.x * floor(dk.x / T.x + 0.5);
  vec2 kn = step(0.55, ph2) * knot(kc + dk, kc, 0.018 + 0.02 * ph);
  // Grain runs along the plank.
  float warp = fbm(vec2(m.x * 0.75, m.y * 6.0), T * vec2(0.75, 6.0), 4) + kn.y * 0.6 * sign(dk.y);
  float grain = fbm(vec2(m.x * 1.5, (m.y + ph * 7.0) * 42.0 + warp * 6.0), T * vec2(1.5, 42.0), 5);
  float rings = abs(fract(grain * 7.0 + ph * 3.0 + kn.y * 1.5) - 0.5) * 2.0;
  float pore = pores(m + ph * 3.0, T);
  // Adze and plane marks across the plank.
  float adze = vnoise(vec2(m.x * 7.0 + ph * 5.0, m.y * 1.5), T * vec2(7.0, 1.5));
  // Treenails: two per strake roughly every metre.
  vec2 tn = vec2(fract(xo / 0.95) - 0.5, fy - 0.5);
  float tnd = length(vec2(tn.x * 0.95, (abs(tn.y) - 0.24) * pw));
  float tnail = 1.0 - smoothstep(0.012, 0.016, tnd);
  float tnRing = (1.0 - smoothstep(0.0, 0.0025, abs(tnd - 0.014)));
  // Iron bolts at the butts, bleeding rust downward.
  float bx = (fx < 0.5 ? fx : fx - 1.0) * len - 0.05;
  float bolt = 1.0 - smoothstep(0.008, 0.012, length(vec2(bx, (fy - 0.5) * pw)));
  float drip = (1.0 - smoothstep(0.0, 0.025 + 0.02 * vnoise(vec2(m.y * 20.0, 1.0), vec2(T.y * 20.0, 1.0)), abs(bx))) * smoothstep(0.5, 0.0, fy)
             * (0.5 + 0.5 * vnoise(m * vec2(40.0, 4.0), T * vec2(40.0, 4.0)));
  float bulge = sin(fy * 3.14159) * 0.004;
  height = bulge - seam * 0.006 + oakum * 0.0015 + (rings - 0.5) * 0.0007 + (adze - 0.5) * 0.0006
         - pore * 0.00035 + tnail * 0.0008 - tnRing * 0.0006 + bolt * 0.002 - kn.x * 0.0012
         - (1.0 - smoothstep(0.0, 0.2, grain)) * 0.0005;
  vec3 oak = mix(vec3(0.17, 0.115, 0.07), vec3(0.34, 0.24, 0.15), ph);
  oak *= 0.85 + 0.12 * ph2;
  oak *= 0.8 + 0.35 * rings;
  oak = mix(oak, oak * 0.55, smoothstep(0.55, 0.9, grain));
  oak *= 1.0 - pore * 0.25;
  oak = mix(oak, vec3(0.07, 0.045, 0.03), kn.x * 0.85);
  // Salt bloom, and grime washed down the side from every seam.
  float salt = smoothstep(0.62, 0.9, fbm(vec2(m.x * 3.0, m.y * 0.75), T * vec2(3.0, 0.75), 5));
  float streak = smoothstep(0.45, 0.85, fbm(vec2(m.x * 26.0, m.y * 1.25), T * vec2(26.0, 1.25), 4)) * (1.0 - fy) * 0.8;
  vec3 col = mix(oak, vec3(0.42, 0.4, 0.36), salt * 0.35);
  col *= 1.0 - streak * 0.35;
  col = mix(col, vec3(0.025, 0.02, 0.016), seam * 0.92);
  col = mix(col, vec3(0.33, 0.27, 0.18), oakum * 0.5);
  col = mix(col, vec3(0.2, 0.13, 0.07), tnail * 0.55);
  col = mix(col, col * 0.5, tnRing * 0.6);
  col = mix(col, vec3(0.05, 0.04, 0.035), bolt);
  col = mix(col, vec3(0.3, 0.12, 0.04), drip * 0.4);
  albedo = col;
  // Paint wears through at plank edges, round the bolts and in scuffs; the
  // chips cluster where lines and boats rub.
  float edge = 1.0 - smoothstep(0.0, 0.02, sd);
  float scuff = fbm(m * vec2(6.0, 14.0), T * vec2(6.0, 14.0), 6);
  float rub = smoothstep(0.55, 0.8, fbm(m * vec2(1.0, 2.5) + 9.0, T * vec2(1.0, 2.5), 3));
  float chips = smoothstep(0.76 - rub * 0.07, 0.8 - rub * 0.07, fbm(m * vec2(18.0, 30.0) + 5.0, T * vec2(18.0, 30.0), 4));
  float boltWear = 1.0 - smoothstep(0.008, 0.02, length(vec2(bx, (fy - 0.5) * pw)));
  wear = clamp(smoothstep(0.74, 0.84, scuff) * 0.6 * rub + chips * 0.6 + edge * 0.3 * smoothstep(0.6, 0.78, scuff) + boltWear * 0.5, 0.0, 1.0);
  float rough = mix(0.72, 0.9, grain) - seam * 0.35 + pore * 0.05 - bolt * 0.2;
  float ao = 1.0 - seam * 0.6 - tnRing * 0.3 - (1.0 - fy * (1.0 - fy) * 4.0) * 0.08;
  orm = vec3(ao, rough, bolt * 0.6);
}
`;

/**
 * Holystoned deck planking: pale scrubbed oak, pitch seams with the odd
 * drip, bungs over the spikes, darker end grain at the butts, scratches from
 * the holystones and dragged gear, a worn path down the middle and water
 * rings where buckets stood.
 */
const DECK = /* glsl */ `
void surface(vec2 m, out vec3 albedo, out float height, out vec3 orm, out float wear) {
  vec2 T = uSize;
  float pw = 0.24;
  float row = floor(m.y / pw);
  float fy = fract(m.y / pw);
  float rh = hash12(vec2(row, 9.0));
  float len = T.x / (1.0 + floor(rh * 1.99));
  float xo = m.x + rh * T.x * 0.5 + row * 1.7;
  float pi = floor(xo / len);
  float fx = fract(xo / len);
  float ph = hash12(vec2(row, pi + 5.0));
  float seamY = min(fy, 1.0 - fy) * pw;
  float seamX = min(fx, 1.0 - fx) * len;
  float seam = 1.0 - smoothstep(0.0015, 0.0055, min(seamY, seamX));
  float endGrain = 1.0 - smoothstep(0.0, 0.05, seamX);
  // Pitch squeezed out of the seams and dripped.
  float pitch = (1.0 - smoothstep(0.004, 0.012 + 0.01 * vnoise(m * vec2(12.0, 3.0), T * vec2(12.0, 3.0)), seamY)) * smoothstep(0.72, 0.8, vnoise(m * vec2(4.0, 30.0), T * vec2(4.0, 30.0)));
  float drips = smoothstep(0.05, 0.02, worleyT(m * 7.0 + 13.0, T * 7.0)) * step(0.8, hash12(floor(m * 7.0 + 13.0)));
  float warp = fbm(vec2(m.x * 0.5, m.y * 5.0), T * vec2(0.5, 5.0), 4);
  float grain = fbm(vec2(m.x * 1.0, (m.y + ph * 4.0) * 50.0 + warp * 5.0), T * vec2(1.0, 50.0), 5);
  float rings = abs(fract(grain * 6.0 + ph * 2.0) - 0.5) * 2.0;
  float pore = pores(m + ph * 7.0, T);
  vec2 tn = vec2(fract(xo / 1.1) - 0.5, fy - 0.5);
  float tnd = length(vec2(tn.x * 1.1, (abs(tn.y) - 0.26) * pw));
  float bung = 1.0 - smoothstep(0.009, 0.013, tnd);
  // Holystone and dragging scratches in a few directions. Each runs along a
  // lattice direction of the tile so the pattern still wraps seamlessly.
  float scr = 0.0;
  for (int k = 0; k < 3; k++) {
    vec2 dir = k == 0 ? vec2(3.0, 1.0) : k == 1 ? vec2(-2.0, 3.0) : vec2(5.0, -2.0);
    float u = dot(m / T, dir);
    float line = vnoise(vec2(u * 90.0, float(k) * 7.0), vec2(90.0, 1e4));
    scr = max(scr, smoothstep(0.93, 0.99, line) * smoothstep(0.4, 0.8, vnoise(m * 1.5 + float(k) * 7.0, T * 1.5)));
  }
  float path = smoothstep(0.35, 0.75, fbm(m * vec2(0.5, 1.0) + 4.0, T * vec2(0.5, 1.0), 3));
  float ringsW = (1.0 - smoothstep(0.0, 0.012, abs(worleyT(m * 1.5 + 3.0, T * 1.5) - 0.32))) * step(0.7, vnoise(m * 1.5 + 3.0, T * 1.5));
  float wearing = fbm(m * (5.0 / 6.0), T * (5.0 / 6.0), 5);
  height = -seam * 0.004 + (rings - 0.5) * 0.0009 + bung * 0.0003 + sin(fy * 3.14159) * 0.0015
         - pore * 0.0003 - scr * 0.0004 + pitch * 0.0006 + drips * 0.0005 - path * (rings - 0.5) * 0.0006;
  vec3 wood = mix(vec3(0.3, 0.25, 0.18), vec3(0.44, 0.38, 0.29), ph);
  wood *= 0.82 + 0.3 * rings;
  wood *= 1.0 - pore * 0.18;
  wood = mix(wood, vec3(0.5, 0.47, 0.42), smoothstep(0.4, 0.8, wearing) * 0.45 + path * 0.2);
  float dirt = smoothstep(0.55, 0.85, fbm(m * (10.0 / 6.0) + 3.0, T * (10.0 / 6.0), 6));
  wood = mix(wood, wood * 0.55, dirt * 0.6);
  wood = mix(wood, wood * 0.62, endGrain * 0.6);
  wood = mix(wood, wood * 1.18 + 0.03, scr * 0.5);
  wood = mix(wood, wood * 0.7, ringsW * 0.5);
  wood = mix(wood, vec3(0.02, 0.018, 0.016), max(seam * 0.95, max(pitch, drips) * 0.9));
  wood = mix(wood, vec3(0.36, 0.3, 0.21), bung * 0.55);
  albedo = wood;
  wear = 0.0;
  float rough = mix(0.78, 0.93, grain) - seam * 0.3 - max(pitch, drips) * 0.45 - path * 0.08;
  orm = vec3(1.0 - seam * 0.5 - endGrain * 0.1, rough, 0.0);
}
`;

/**
 * Dark weathered timber for spars, rails, carriages: grain, knots, checks and
 * a worn tar coat. Spars and turned parts carry u around and v along their
 * length, so the grain runs along v (the tile is square, so it still wraps).
 */
const TIMBER = /* glsl */ `
void surface(vec2 mUv, out vec3 albedo, out float height, out vec3 orm, out float wear) {
  vec2 T = uSize;
  vec2 m = mUv.yx;
  // Knots scattered along the length.
  vec2 kcell = floor(m / vec2(0.5, 0.25));
  vec2 kc = (kcell + 0.25 + 0.5 * hash22(mod(kcell, T / vec2(0.5, 0.25)))) * vec2(0.5, 0.25);
  vec2 kn = step(0.65, hash12(kcell + 3.0)) * knot(m, kc, 0.015 + 0.015 * hash12(kcell));
  float warp = fbm(m * vec2(1.0, 3.0), T * vec2(1.0, 3.0), 4) + kn.y * 0.5 * sign(m.y - kc.y);
  float grain = fbm(vec2(m.x * 2.0, m.y * 38.0 + warp * 8.0), T * vec2(2.0, 38.0), 5);
  float rings = abs(fract(grain * 9.0 + kn.y * 2.0) - 0.5) * 2.0;
  float pore = pores(m, T);
  // Seasoning checks: long thin splits along the grain.
  float cracks = 1.0 - smoothstep(0.0, 0.02, abs(fbm(vec2(m.x * 1.5, m.y * 14.0), T * vec2(1.5, 14.0), 3) - 0.5));
  cracks *= smoothstep(0.55, 0.75, fbm(m * 2.0 + 7.0, T * 2.0, 3));
  height = (rings - 0.5) * 0.001 - cracks * 0.002 - pore * 0.0003 - kn.x * 0.001;
  vec3 c = mix(vec3(0.18, 0.12, 0.07), vec3(0.33, 0.23, 0.14), rings);
  c = mix(c, c * 0.6, smoothstep(0.5, 0.9, grain));
  c *= 1.0 - pore * 0.2;
  c = mix(c, vec3(0.06, 0.04, 0.025), kn.x * 0.8);
  c = mix(c, vec3(0.05, 0.035, 0.025), cracks * 0.8);
  float grime = smoothstep(0.5, 0.85, fbm(m * 1.5 + 11.0, T * 1.5, 5));
  c = mix(c, c * 0.5, grime * 0.5);
  // Old tar and varnish: darker and glossier where it survives.
  float tar = smoothstep(0.45, 0.7, fbm(m * vec2(2.5, 6.0) + 21.0, T * vec2(2.5, 6.0), 4));
  c = mix(c, c * 0.62, tar * 0.5);
  albedo = c;
  wear = clamp(smoothstep(0.64, 0.82, fbm(m * vec2(5.0, 11.0) + 2.0, T * vec2(5.0, 11.0), 6)) + cracks * 0.4, 0.0, 1.0);
  orm = vec3(1.0 - cracks * 0.4, mix(0.68, 0.88, grain) - tar * 0.25, 0.0);
}
`;

/**
 * Sailcloth: a real over-and-under weave with uneven slubbed threads, cloths
 * about 0.6 m wide joined by double-stitched seams, darned patches, mildew
 * spotting, rust and tar stains, and sun-bleached and grimy areas.
 */
const CANVAS = /* glsl */ `
void surface(vec2 m, out vec3 albedo, out float height, out vec3 orm, out float wear) {
  vec2 T = uSize;
  // Weave: warp (along v) and weft (along u) threads crossing over and under.
  vec2 t = m * 110.0;
  vec2 ti = floor(t), tf = fract(t);
  float over = mod(ti.x + ti.y, 2.0);
  float warpT = sin(tf.x * 3.14159);
  float weftT = sin(tf.y * 3.14159);
  float slubW = 0.75 + 0.5 * hash12(vec2(ti.x, 1.0) + floor(t.y / 37.0));
  float slubF = 0.75 + 0.5 * hash12(vec2(ti.y, 2.0) + floor(t.x / 29.0));
  float weave = mix(warpT * slubW * (0.6 + 0.4 * weftT), weftT * slubF * (0.6 + 0.4 * warpT), over);
  // Vertical cloths ~0.6 m wide with double-stitched seams.
  float cw = T.x / floor(T.x / 0.6 + 0.5);
  float fx = fract(m.x / cw);
  float seamD = min(fx, 1.0 - fx) * cw;
  float seam = 1.0 - smoothstep(0.012, 0.02, seamD);
  float stitchRow = min(abs(seamD - 0.005), abs(seamD - 0.015));
  float stitch = seam * step(0.45, fract(m.y * 90.0)) * (1.0 - smoothstep(0.0012, 0.0024, stitchRow));
  float cloth = floor(m.x / cw);
  float ch = hash12(vec2(cloth, 2.0));
  // Patches, sewn round the edge.
  vec2 pc = floor(m / vec2(cw, 1.1));
  float hasPatch = step(0.94, hash12(pc + 40.0));
  vec2 pf = fract(m / vec2(cw, 1.1));
  vec2 p0 = vec2(0.12 + 0.1 * hash12(pc + 3.0), 0.2 + 0.1 * hash12(pc + 5.0));
  vec2 p1 = vec2(0.88 - 0.1 * hash12(pc + 7.0), 0.75 - 0.1 * hash12(pc + 9.0));
  float patchM = hasPatch * step(p0.x, pf.x) * step(pf.x, p1.x) * step(p0.y, pf.y) * step(pf.y, p1.y);
  float pEdge = min(min(abs(pf.x - p0.x), abs(pf.x - p1.x)) * cw, min(abs(pf.y - p0.y), abs(pf.y - p1.y)) * 1.1);
  float patchEdge = hasPatch * (1.0 - smoothstep(0.0, 0.02, pEdge));
  float patchStitch = patchEdge * step(0.5, fract((m.x + m.y) * 80.0)) * (1.0 - smoothstep(0.004, 0.007, abs(pEdge - 0.01)));
  // Stains and weathering.
  vec2 k = 1.0 / T;
  float grime = fbm(m * k * 4.0, vec2(4.0), 6);
  float stain = smoothstep(0.55, 0.8, fbm(m * k * 9.0 + 5.0, vec2(9.0), 5));
  float bleach = smoothstep(0.35, 0.75, fbm(m * k * 2.0 + 17.0, vec2(2.0), 3));
  float mildew = (1.0 - smoothstep(0.02, 0.06, worleyT(m * k * 168.0, vec2(168.0)))) * smoothstep(0.6, 0.8, fbm(m * k * 5.0 + 31.0, vec2(5.0), 3));
  float rustS = smoothstep(0.78, 0.9, fbm(m * k * vec2(25.0, 6.0) + 41.0, vec2(25.0, 6.0), 4));
  height = weave * 0.00032 + seam * 0.0006 + patchM * 0.0004 + (stitch + patchStitch) * 0.0002;
  vec3 c = mix(vec3(0.69, 0.64, 0.54), vec3(0.74, 0.69, 0.59), ch);
  c *= 0.86 + 0.14 * weave;
  c = mix(c, c * 0.82, seam * 0.6);
  c = mix(c, vec3(0.58, 0.53, 0.43), patchM * 0.7);
  c = mix(c, c * 0.78, patchEdge);
  c = mix(c, c * 0.7, (stitch + patchStitch) * 0.8);
  c = mix(c, c * vec3(0.86, 0.82, 0.74), stain * 0.45);
  c = mix(c, c * 1.08 + 0.02, bleach * 0.35);
  c = mix(c, c * 0.85, smoothstep(0.4, 0.8, grime) * 0.5);
  c = mix(c, vec3(0.2, 0.2, 0.15), mildew * 0.5);
  c = mix(c, c * vec3(0.8, 0.6, 0.45), rustS * 0.5);
  albedo = c;
  wear = 0.0;
  orm = vec3(1.0 - seam * 0.2 - (1.0 - weave) * 0.15, 0.92, 0.0);
}
`;

/** Three-strand laid rope: u around (1 m = circumference unit), v along; tarred fibres, some frayed. */
const ROPE = /* glsl */ `
void surface(vec2 m, out vec3 albedo, out float height, out vec3 orm, out float wear) {
  vec2 T = uSize;
  // Strands spiral: phase advances with length.
  float s = fract(m.x / T.x * 3.0 + m.y / T.y * 3.0);
  float strand = sin(s * 3.14159);
  // Yarns within each strand twist the other way.
  float yarn = 0.5 + 0.5 * sin((m.x / T.x * 18.0 - m.y / T.y * 9.0) * 6.28318);
  float fib = fbm(vec2(m.x * 40.0 + m.y * 40.0, m.y * 3.0), T * vec2(40.0, 3.0), 3);
  float fray = smoothstep(0.75, 0.9, vnoise(vec2(m.x * 80.0, m.y * 200.0), T * vec2(80.0, 200.0)));
  height = strand * 0.004 + yarn * 0.0005 + fib * 0.0006 + fray * 0.0003;
  float tar = smoothstep(0.35, 0.7, fbm(vec2(m.y * 5.0, m.x * 10.0), vec2(2.0, 1.0), 3));
  vec3 c = mix(vec3(0.24, 0.18, 0.11), vec3(0.42, 0.33, 0.21), strand * 0.6 + fib * 0.25 + yarn * 0.15);
  c = mix(c, c * 0.65, tar * 0.4);
  c = mix(c, vec3(0.5, 0.42, 0.3), fray * 0.4);
  albedo = c;
  wear = 0.0;
  orm = vec3(0.5 + 0.5 * strand, 0.88 - tar * 0.12, 0.0);
}
`;

/** Cast iron: hammered and pitted, black paint flaking at the edges with rust haloes. */
const IRON = /* glsl */ `
void surface(vec2 m, out vec3 albedo, out float height, out vec3 orm, out float wear) {
  vec2 T = uSize;
  float pits = worleyT(m * 22.0, T * 22.0);
  float hammer = worleyT(m * 6.0 + 3.0, T * 6.0);
  float rust = smoothstep(0.5, 0.78, fbm(m * (8.0 / 3.0), T * (8.0 / 3.0), 6));
  float chipN = fbm(m * (20.0 / 3.0) + 3.0, T * (20.0 / 3.0), 5);
  float chip = smoothstep(0.6, 0.66, chipN);
  // A rusty halo just outside each flake of lost paint.
  float halo = smoothstep(0.54, 0.6, chipN) * (1.0 - chip);
  float flakeEdge = smoothstep(0.58, 0.6, chipN) * (1.0 - smoothstep(0.6, 0.62, chipN));
  height = -(1.0 - smoothstep(0.0, 0.25, pits)) * 0.0006 + rust * 0.0004 + hammer * hammer * 0.0008
         - chip * 0.00025 + flakeEdge * 0.0002;
  vec3 paint = vec3(0.03, 0.03, 0.032) * (0.9 + 0.2 * vnoise(m * 30.0, T * 30.0));
  vec3 metal = vec3(0.28, 0.27, 0.26) * (0.85 + 0.3 * hammer);
  vec3 rustC = mix(vec3(0.24, 0.09, 0.03), vec3(0.4, 0.18, 0.07), fbm(m * 12.0, T * 12.0, 3));
  vec3 c = mix(paint, metal, chip);
  c = mix(c, rustC, max(rust * 0.85, halo * 0.6));
  albedo = c;
  wear = 0.0;
  float metalness = chip * (1.0 - rust);
  orm = vec3(1.0 - (1.0 - smoothstep(0.0, 0.2, pits)) * 0.3, mix(0.55, 0.9, max(rust, halo)) - chip * 0.2, metalness);
}
`;

export class TextureLibrary {
  readonly hull: TextureSet;
  readonly deck: TextureSet;
  readonly timber: TextureSet;
  readonly canvas: TextureSet;
  readonly rope: TextureSet;
  readonly iron: TextureSet;

  constructor(renderer: THREE.WebGLRenderer, hi: boolean) {
    // Roughly double the texel density on GPUs that can take it (a large
    // max texture size is a fair proxy for a discrete card with memory to
    // spare, about 350 MB here); the earlier sizes otherwise.
    const big = hi && renderer.capabilities.maxTextureSize >= 8192;
    const t = big
      ? { hull: [4096, 2048], deck: 2560, timber: 2048, canvas: 2560, rope: [256, 1024], iron: 2048 }
      : hi
        ? { hull: [2048, 1024], deck: 1536, timber: 1024, canvas: 2048, rope: [128, 512], iron: 1024 }
        : { hull: [1024, 512], deck: 768, timber: 512, canvas: 1024, rope: [128, 256], iron: 512 };
    this.hull = bake(renderer, HULL, t.hull[0]!, t.hull[1]!, [8, 4]);
    this.deck = bake(renderer, DECK, t.deck, t.deck, [6, 6]);
    this.timber = bake(renderer, TIMBER, t.timber, t.timber, [2, 2]);
    this.canvas = bake(renderer, CANVAS, t.canvas, t.canvas, [4.2, 4.2]);
    this.rope = bake(renderer, ROPE, t.rope[0]!, t.rope[1]!, [0.1, 0.4]);
    this.iron = bake(renderer, IRON, t.iron, t.iron, [1.5, 1.5]);
  }
}
